import { DatasetName } from "./constants.ts";
import { MeasureName } from "./measures.ts";
import { DimensionName } from "./dimensions.ts";
import type { FilterName } from "./filters.ts";
import type { ISODate } from "./dateUtils.ts";

export type ChartTypeName =
  | "Bar"
  | "Insights Wheel"
  | "Line"
  | "Horizontal Bar"
  | "Horizontal Stacked Bar"
  | "Stacked Bar"
  | "Pie"
  | "Funnel";

export type ChartConfiguration = {
  label: ChartTypeName;
  requiresDimension?: DimensionName[];
  requiresMeasure?: MeasureName;
  restrictToDatasets?: DatasetName[];
};

export const CHART_TYPES_V2: Record<string, ChartConfiguration> = {
  BAR: { label: "Bar" },
  INSIGHTS_WHEEL: {
    label: "Insights Wheel",
    requiresMeasure: "HEADCOUNT",
    restrictToDatasets: ["main"],
  },
  LINE: { label: "Line" },
  HORIZONTAL_BAR: { label: "Horizontal Bar" },
  HORIZONTAL_STACKED_BAR: { label: "Horizontal Stacked Bar" },
  STACKED_BAR: { label: "Stacked Bar" },
  PIE: { label: "Pie" },
  FUNNEL: {
    label: "Funnel",
    requiresDimension: ["RECRUITMENT_STAGE_HISTORICAL", "RECRUITMENT_STAGE"],
    requiresMeasure: "APPLICANT_COUNT",
    restrictToDatasets: ["recruitment_applications"],
  },
};

export const CHART_TYPES_BY_LABEL: Record<ChartTypeName, ChartConfiguration> = {
  Bar: CHART_TYPES_V2.BAR,
  Line: CHART_TYPES_V2.LINE,
  "Horizontal Bar": CHART_TYPES_V2.HORIZONTAL_BAR,
  "Horizontal Stacked Bar": CHART_TYPES_V2.HORIZONTAL_STACKED_BAR,
  "Stacked Bar": CHART_TYPES_V2.STACKED_BAR,
  "Insights Wheel": CHART_TYPES_V2.INSIGHTS_WHEEL,
  Pie: CHART_TYPES_V2.PIE,
  Funnel: CHART_TYPES_V2.FUNNEL,
};

// Simple union type for all possible operators
export type FilterOperator =
  // String operators
  | "IN"
  | "EQUALS"
  | "NOT_EQUALS"
  | "CONTAINS"
  // Special operator
  | "SELECT"
  // Number operators
  | "GREATER_THAN"
  | "LESS_THAN"
  | "LESS_THAN_OR_EQUAL"
  | "GREATER_THAN_OR_EQUAL"
  | "BETWEEN"
  // Date operators
  | "ALL"
  | "BEFORE"
  | "AFTER"
  | "LAST_30_DAYS"
  | "LAST_90_DAYS"
  | "LAST_180_DAYS"
  | "LAST_365_DAYS";
// Boolean operators use EQUALS

// Operator configurations with labels for UI
export const FILTER_OPERATOR_CONFIG: Record<
  FilterOperator,
  { label: string; fieldTypes: FieldType[] }
> = {
  IN: { label: "Is one of", fieldTypes: ["string", "special:string"] },
  EQUALS: { label: "Equals", fieldTypes: ["string", "number", "boolean"] },
  NOT_EQUALS: {
    label: "Is not",
    fieldTypes: ["string", "number", "boolean"],
  },
  CONTAINS: { label: "Contains", fieldTypes: ["string"] },
  SELECT: { label: "Equals", fieldTypes: ["select"] },
  GREATER_THAN: {
    label: "Greater than",
    fieldTypes: ["number", "special:number"],
  },
  GREATER_THAN_OR_EQUAL: {
    label: "Greater than or equals",
    fieldTypes: ["number", "special:number"],
  },
  LESS_THAN: { label: "Less than", fieldTypes: ["number", "special:number"] },
  LESS_THAN_OR_EQUAL: {
    label: "Less than or equals",
    fieldTypes: ["number", "special:number"],
  },
  BETWEEN: {
    label: "Between",
    fieldTypes: ["number", "date", "special:number"],
  },
  ALL: { label: "All Time", fieldTypes: ["date"] },
  BEFORE: { label: "Before", fieldTypes: ["date"] },
  AFTER: { label: "After", fieldTypes: ["date"] },
  LAST_30_DAYS: { label: "Last 30 days", fieldTypes: ["date"] },
  LAST_90_DAYS: { label: "Last 90 days", fieldTypes: ["date"] },
  LAST_180_DAYS: { label: "Last 180 days", fieldTypes: ["date"] },
  LAST_365_DAYS: { label: "Last 365 days", fieldTypes: ["date"] },
};

// Helper function to get valid operators for a field type
export function getOperatorsForFieldType(fieldType: FieldType) {
  return Object.entries(FILTER_OPERATOR_CONFIG)
    .filter(([, config]) => config.fieldTypes.includes(fieldType))
    .map(([operator, config]) => ({
      value: operator as FilterOperator,
      label: config.label,
    }));
}

// Helper to check if an operator is valid for a field type
export function isValidOperatorForFieldType(
  operator: FilterOperator,
  fieldType: FieldType,
): boolean {
  return FILTER_OPERATOR_CONFIG[operator].fieldTypes.includes(fieldType);
}

// Helper to check if an operator is a relative date operator
export function isRelativeDateOperator(operator: FilterOperator): boolean {
  return [
    "LAST_30_DAYS",
    "LAST_90_DAYS",
    "LAST_180_DAYS",
    "LAST_365_DAYS",
  ].includes(operator);
}

export function isComparisonDateOperator(operator: FilterOperator): boolean {
  return operator === "BETWEEN" || isRelativeDateOperator(operator);
}

// Helper to check if an operator is an absolute date operator
export function isAbsoluteDateOperator(operator: FilterOperator): boolean {
  return ["BETWEEN", "BEFORE", "AFTER", "ALL"].includes(operator);
}

// Helper to check if operator is "ALL" (for date filters)
export function isAllOperator(operator: FilterOperator): boolean {
  return operator === "ALL";
}

// Note that "Day" is not available to users and is only used internally for a custom snapshot for a date filter on the main dataset without a time dimension.
export type TimeCadence = "Day" | "Week" | "Month" | "Quarter" | "Year";

export interface TimeDimension {
  field: string;
  cadence: TimeCadence;
}

// Data types from dataset schema
// Special indicates fields that require special handling (e.g. measures that are calculated on the fly)
export type FieldType =
  | "string"
  | "number"
  | "boolean"
  | "date"
  | "special:string"
  | "special:number"
  | "select"; // used on the attrition prob timeframe

type ReportFilterValue = string | number | boolean | string[] | number[];

type ReportFilterSecondaryValue = number | string;

export interface ReportFilterV2 {
  filterDefinitionName: FilterName;
  operator: FilterOperator;
  value: ReportFilterValue;
  secondaryValue?: ReportFilterSecondaryValue; // For BETWEEN operations
}

export interface ReportV2 {
  id: string;
  title: string;
  config: ReportConfigV2;
  created_at: Date;
  updated_at: Date;
  account_id: string;
  user_id: string;
  system_report: boolean;
  system_report_name?: string;
}

export interface ReportConfigV2 {
  id?: string;
  chart: ReportChartSettingsV2;
  data: ReportDataSettingsV2;
  metadata: ReportMetadataV2;
  title: string;
  _titleManuallySet?: boolean; // Flag to track if title has been manually edited
  system_report_name?: string; // Name of the system report if this is a system report
}

export interface ReportMetadataV2 {
  ai_headline: string | null;
  ai_summary: string | null;
  ai_headline_dirty: boolean; // the dirty flag represents drift from the last data setting, not whether the headline has changed
  ai_summary_dirty: boolean;
  ai_headline_expanded: boolean; // Flag to track if AI headline is expanded in UI
  ai_summary_expanded: boolean; // Flag to track if AI summary is expanded in UI
}

export interface ReportExtras {
  avgTimeToFill?: number;
  jobReqCount?: number;
  attritionRate?: number;
  rollup_hires?: number;
  rollup_terminations?: number;
  rollup_promotions?: number;
  methodologyNotes?: Record<string, string[]>;
  dataUnavailable?: true;
}

type LegendItemOverride = {
  color?: string;
  label?: string;
  hidden?: boolean;
};

export interface ReportChartSettingsV2 {
  chartType: ChartTypeName;
  hideSmallGroups?: boolean;
  includeLegend: boolean;
  legendItems: Record<string, LegendItemOverride>;
  legendPosition: "top" | "bottom" | "left" | "right";
  limit: number;
  // When true, charts/tables display numeric counts (value_num) instead of percentages (value_pct)
  showCount: boolean;
  xAxisLabelOverride: string; // For the x-axis label in combo charts
  yAxisLabelOverride1: string; // For the left y-axis in combo charts
  yAxisLabelOverride2: string; // For the right y-axis in combo charts
}

export interface ReportDataSettingsV2 {
  datasetName: DatasetName;
  measures: MeasureName[];
  timeCadence: TimeCadence;
  filters: ReportFilterV2[];
  dimensions: DimensionName[];
  includeProjections: boolean;
  comparisonEnabled: boolean;
  compareWith: "previousPeriod" | "oneYearAgo" | "custom";
  compareCustomPeriodStart?: ISODate;
  compareCustomPeriodEnd?: ISODate;
}

const REPRESENTATION_REPORT: ReportConfigV2 = {
  title: "Representation by Department by Gender",
  data: {
    datasetName: "main",
    measures: ["HEADCOUNT"],
    dimensions: ["DEPARTMENT", "GENDER"],
    filters: [],
    timeCadence: "Quarter",
    includeProjections: false,
    comparisonEnabled: false,
    compareWith: "previousPeriod",
  },
  chart: {
    chartType: "Bar",
    hideSmallGroups: false,
    includeLegend: true,
    legendItems: {},
    legendPosition: "bottom",
    limit: 10,
    showCount: true,
    xAxisLabelOverride: "",
    yAxisLabelOverride1: "",
    yAxisLabelOverride2: "",
  },
  metadata: {
    ai_headline:
      "Diverse Workforce Insights: Gender Distribution Across Departments Revealed",
    ai_summary:
      "The latest HR report highlights a diverse workforce with a notable gender distribution across various departments. Female employees represent a significant portion of the workforce, with departments such as Department 1 and Department 30 exhibiting a balanced gender mix. In contrast, certain departments show a predominantly male workforce, indicating areas for potential improvement in gender inclusivity.\n\nInterestingly, the data reveals that some departments, like Department 50 and Department 34, have a strong representation of female employees, while others, like Department 2 and Department 42, have a considerable male presence. The presence of non-binary employees in various departments also points to a growing recognition of diversity in gender identities, which is essential for fostering an inclusive workplace culture.\n\nOverall, these findings underscore the importance of monitoring gender distribution and promoting diversity across departments. Organizations can leverage these insights to implement targeted initiatives aimed at enhancing gender balance and creating a more equitable workplace for all employees.",
    ai_headline_dirty: false,
    ai_summary_dirty: false,
    ai_headline_expanded: true,
    ai_summary_expanded: true,
  },
};

const HIRES_REPORT: ReportConfigV2 = {
  title: "Hires by Hire Date by Department",
  _titleManuallySet: false,
  data: {
    datasetName: "main",
    measures: ["HIRES"],
    dimensions: ["DATE", "DEPARTMENT"],
    filters: [],
    timeCadence: "Month",
    includeProjections: false,
    comparisonEnabled: false,
    compareWith: "previousPeriod",
  },
  chart: {
    chartType: "Line",
    hideSmallGroups: false,
    includeLegend: true,
    legendItems: {},
    legendPosition: "bottom",
    limit: 10,
    showCount: true,
    xAxisLabelOverride: "",
    yAxisLabelOverride1: "",
    yAxisLabelOverride2: "",
  },
  metadata: {
    ai_headline: null,
    ai_summary: null,
    ai_headline_dirty: true,
    ai_summary_dirty: true,
    ai_headline_expanded: true,
    ai_summary_expanded: true,
  },
};

const COMPENSATION_REPORT: ReportConfigV2 = {
  title: "Compensation by Department by Pay Band",
  _titleManuallySet: false,
  data: {
    datasetName: "main",
    measures: ["AVG_COMPENSATION"],
    dimensions: ["DEPARTMENT", "PAY_BAND"],
    filters: [],
    timeCadence: "Quarter",
    includeProjections: false,
    comparisonEnabled: false,
    compareWith: "previousPeriod",
  },
  chart: {
    chartType: "Bar",
    hideSmallGroups: false,
    includeLegend: true,
    legendItems: {},
    legendPosition: "bottom",
    limit: 10,
    showCount: true,
    xAxisLabelOverride: "",
    yAxisLabelOverride1: "",
    yAxisLabelOverride2: "",
  },
  metadata: {
    ai_headline: null,
    ai_summary: null,
    ai_headline_dirty: true,
    ai_summary_dirty: true,
    ai_headline_expanded: true,
    ai_summary_expanded: true,
  },
};

const PROMOTIONS_REPORT: ReportConfigV2 = {
  title: "Promotions by Date of Promotion by Department",
  _titleManuallySet: false,
  data: {
    datasetName: "main",
    measures: ["PROMOTIONS"],
    dimensions: ["DATE", "DEPARTMENT"],
    filters: [],
    timeCadence: "Month",
    includeProjections: false,
    comparisonEnabled: false,
    compareWith: "previousPeriod",
  },
  chart: {
    chartType: "Line",
    hideSmallGroups: false,
    includeLegend: true,
    legendItems: {},
    legendPosition: "bottom",
    limit: 10,
    showCount: true,
    xAxisLabelOverride: "",
    yAxisLabelOverride1: "",
    yAxisLabelOverride2: "",
  },
  metadata: {
    ai_headline: null,
    ai_summary: null,
    ai_headline_dirty: true,
    ai_summary_dirty: true,
    ai_headline_expanded: true,
    ai_summary_expanded: true,
  },
};

const TERMINATIONS_REPORT: ReportConfigV2 = {
  title: "Terminations by Termination Date by Termination Reason",
  _titleManuallySet: false,
  data: {
    datasetName: "main",
    measures: ["TERMINATIONS"],
    dimensions: ["DATE", "TERMINATION_REASON"],
    filters: [],
    timeCadence: "Quarter",
    includeProjections: false,
    comparisonEnabled: false,
    compareWith: "previousPeriod",
  },
  chart: {
    chartType: "Line",
    hideSmallGroups: false,
    includeLegend: true,
    legendItems: {},
    legendPosition: "bottom",
    limit: 10,
    showCount: true,
    xAxisLabelOverride: "",
    yAxisLabelOverride1: "",
    yAxisLabelOverride2: "",
  },
  metadata: {
    ai_headline: null,
    ai_summary: null,
    ai_headline_dirty: true,
    ai_summary_dirty: true,
    ai_headline_expanded: true,
    ai_summary_expanded: true,
  },
};

const ATTRITION_RATE_REPORT: ReportConfigV2 = {
  title: "Attrition Rate by Date",
  _titleManuallySet: false,
  data: {
    datasetName: "main",
    measures: ["ATTRITION_RATE"],
    dimensions: ["DATE"],
    filters: [],
    timeCadence: "Quarter",
    includeProjections: false,
    comparisonEnabled: false,
    compareWith: "previousPeriod",
  },
  chart: {
    chartType: "Line",
    hideSmallGroups: false,
    includeLegend: true,
    legendItems: {},
    legendPosition: "bottom",
    limit: 10,
    showCount: true,
    xAxisLabelOverride: "",
    yAxisLabelOverride1: "",
    yAxisLabelOverride2: "",
  },
  metadata: {
    ai_headline: null,
    ai_summary: null,
    ai_headline_dirty: true,
    ai_summary_dirty: true,
    ai_headline_expanded: true,
    ai_summary_expanded: true,
  },
};

const ATTRITION_PROBABILITY_REPORT: ReportConfigV2 = {
  title: "Attrition Probability by Department",
  _titleManuallySet: false,
  data: {
    datasetName: "main",
    measures: ["ATTRITION_PROBABILITY"],
    dimensions: ["DEPARTMENT"],
    filters: [
      {
        filterDefinitionName: "ATTRITION_PROBABILITY_TIME_HORIZON",
        operator: "EQUALS",
        value: "attrition_6m",
      },
    ],
    timeCadence: "Quarter",
    includeProjections: false,
    comparisonEnabled: false,
    compareWith: "previousPeriod",
  },
  chart: {
    chartType: "Bar",
    hideSmallGroups: false,
    includeLegend: true,
    legendItems: {},
    legendPosition: "bottom",
    limit: 10,
    showCount: true,
    xAxisLabelOverride: "",
    yAxisLabelOverride1: "",
    yAxisLabelOverride2: "",
  },
  metadata: {
    ai_headline: null,
    ai_summary: null,
    ai_headline_dirty: true,
    ai_summary_dirty: true,
    ai_headline_expanded: true,
    ai_summary_expanded: true,
  },
};

const COST_OF_ATTRITION_REPORT: ReportConfigV2 = {
  title: "Cost of Attrition by Termination Date",
  _titleManuallySet: false,
  data: {
    datasetName: "main",
    measures: ["COST_OF_ATTRITION"],
    dimensions: ["DATE"],
    filters: [],
    timeCadence: "Month",
    includeProjections: false,
    comparisonEnabled: false,
    compareWith: "previousPeriod",
  },
  chart: {
    chartType: "Bar",
    hideSmallGroups: false,
    includeLegend: true,
    legendItems: {},
    legendPosition: "bottom",
    limit: 10,
    showCount: true,
    xAxisLabelOverride: "",
    yAxisLabelOverride1: "",
    yAxisLabelOverride2: "",
  },
  metadata: {
    ai_headline: "",
    ai_summary: "",
    ai_headline_dirty: false,
    ai_summary_dirty: false,
    ai_headline_expanded: true,
    ai_summary_expanded: true,
  },
};

const RETENTION_RATE_REPORT: ReportConfigV2 = {
  title: "Retention Rate by Date",
  _titleManuallySet: false,
  data: {
    datasetName: "main",
    measures: ["RETENTION_RATE"],
    dimensions: ["DATE"],
    filters: [],
    timeCadence: "Year",
    includeProjections: false,
    comparisonEnabled: false,
    compareWith: "previousPeriod",
  },
  chart: {
    chartType: "Line",
    hideSmallGroups: false,
    includeLegend: true,
    legendItems: {},
    legendPosition: "bottom",
    limit: 10,
    showCount: true,
    xAxisLabelOverride: "",
    yAxisLabelOverride1: "",
    yAxisLabelOverride2: "",
  },
  metadata: {
    ai_headline: null,
    ai_summary: null,
    ai_headline_dirty: true,
    ai_summary_dirty: true,
    ai_headline_expanded: true,
    ai_summary_expanded: true,
  },
};

const BENCHMARK_REPORT: ReportConfigV2 = {
  title: "Benchmark by Data Source by Race",
  data: {
    datasetName: "benchmark",
    measures: ["BENCHMARK_HEADCOUNT"],
    dimensions: ["RACE"],
    filters: [
      {
        filterDefinitionName: "RACE_GROUPING",
        operator: "EQUALS",
        value: ["Show All Races"],
      },
      {
        filterDefinitionName: "COUNTRY",
        operator: "EQUALS",
        value: ["United States"],
      },
      {
        filterDefinitionName: "STATE",
        operator: "EQUALS",
        value: ["National"],
      },
      {
        filterDefinitionName: "INDUSTRY",
        operator: "EQUALS",
        value: ["All industries"],
      },
      {
        filterDefinitionName: "COMPANY_SIZE",
        operator: "EQUALS",
        value: ["All company sizes"],
      },
    ],
    timeCadence: "Year",
    includeProjections: false,
    comparisonEnabled: false,
    compareWith: "previousPeriod",
  },
  chart: {
    chartType: "Stacked Bar",
    includeLegend: true,
    legendPosition: "bottom",
    legendItems: {},
    limit: 10,
    hideSmallGroups: false,
    showCount: true,
    xAxisLabelOverride: "",
    yAxisLabelOverride1: "",
    yAxisLabelOverride2: "",
  },
  metadata: {
    ai_headline: null,
    ai_summary: null,
    ai_headline_dirty: false,
    ai_summary_dirty: false,
    ai_headline_expanded: true,
    ai_summary_expanded: true,
  },
};

const APPLICANT_FUNNEL_REPORT: ReportConfigV2 = {
  title: "Applicant Funnel (Past Year)",
  data: {
    datasetName: "recruitment_applications",
    measures: ["APPLICANT_COUNT"],
    dimensions: ["RECRUITMENT_STAGE_HISTORICAL"],
    filters: [
      {
        filterDefinitionName: "JOB_REQ_FILL_DATE",
        operator: "LAST_365_DAYS",
        value: "",
      },
    ],
    timeCadence: "Quarter",
    includeProjections: false,
    comparisonEnabled: false,
    compareWith: "previousPeriod",
  },
  chart: {
    chartType: "Funnel",
    includeLegend: true,
    legendPosition: "bottom",
    legendItems: {},
    limit: 10,
    hideSmallGroups: false,
    showCount: true,
    xAxisLabelOverride: "",
    yAxisLabelOverride1: "",
    yAxisLabelOverride2: "",
  },
  metadata: {
    ai_headline: null,
    ai_summary: null,
    ai_headline_dirty: false,
    ai_summary_dirty: false,
    ai_headline_expanded: true,
    ai_summary_expanded: true,
  },
};

const APPLICANT_FUNNEL_CURRENT_REPORT: ReportConfigV2 = {
  title: "Applicant Funnel (Current)",
  data: {
    datasetName: "recruitment_applications",
    measures: ["APPLICANT_COUNT"],
    dimensions: ["RECRUITMENT_STAGE"],
    filters: [],
    timeCadence: "Quarter",
    includeProjections: false,
    comparisonEnabled: false,
    compareWith: "previousPeriod",
  },
  chart: {
    chartType: "Funnel",
    includeLegend: true,
    legendPosition: "bottom",
    legendItems: {},
    limit: 10,
    hideSmallGroups: false,
    showCount: true,
    xAxisLabelOverride: "",
    yAxisLabelOverride1: "",
    yAxisLabelOverride2: "",
  },
  metadata: {
    ai_headline: null,
    ai_summary: null,
    ai_headline_dirty: false,
    ai_summary_dirty: false,
    ai_headline_expanded: true,
    ai_summary_expanded: true,
  },
};

const ATTRITION_DRIVERS_REPORT: ReportConfigV2 = {
  title: "Attrition Top Drivers",
  data: {
    datasetName: "attrition_drivers",
    measures: ["ATTRITION_DRIVERS"],
    dimensions: [],
    filters: [],
    timeCadence: "Quarter",
    includeProjections: false,
    comparisonEnabled: false,
    compareWith: "previousPeriod",
  },
  chart: {
    chartType: "Bar",
    includeLegend: true,
    legendPosition: "bottom",
    legendItems: {},
    limit: 30, // show all drivers
    hideSmallGroups: false,
    showCount: true,
    xAxisLabelOverride: "",
    yAxisLabelOverride1: "",
    yAxisLabelOverride2: "",
  },
  metadata: {
    ai_headline: null,
    ai_summary: null,
    ai_headline_dirty: false,
    ai_summary_dirty: false,
    ai_headline_expanded: true,
    ai_summary_expanded: true,
  },
};

const TIME_TO_FILL_REPORT: ReportConfigV2 = {
  title: "Avg. Time to Fill and Job Requisition Count by Fill Date",
  data: {
    datasetName: "recruitment_applications",
    measures: ["AVG_TIME_TO_FILL", "JOB_REQ_COUNT"],
    dimensions: ["JOB_REQ_FILL_DATE"],
    filters: [],
    timeCadence: "Quarter",
    includeProjections: false,
    comparisonEnabled: false,
    compareWith: "previousPeriod",
  },
  chart: {
    chartType: "Line",
    includeLegend: true,
    legendPosition: "bottom",
    legendItems: {},
    limit: 10,
    hideSmallGroups: false,
    showCount: true,
    xAxisLabelOverride: "",
    yAxisLabelOverride1: "",
    yAxisLabelOverride2: "",
  },
  metadata: {
    ai_headline: null,
    ai_summary: null,
    ai_headline_dirty: false,
    ai_summary_dirty: false,
    ai_headline_expanded: true,
    ai_summary_expanded: true,
  },
};

const JOB_REQUISITION_METRICS_REPORT: ReportConfigV2 = {
  title: "Job Requisition Metrics by Cost Center",
  data: {
    datasetName: "recruitment_applications",
    measures: ["JOB_REQ_COUNT"],
    dimensions: ["JOB_FAMILY"],
    filters: [],
    timeCadence: "Quarter",
    includeProjections: false,
    comparisonEnabled: false,
    compareWith: "previousPeriod",
  },
  chart: {
    chartType: "Bar",
    includeLegend: true,
    legendPosition: "bottom",
    legendItems: {},
    limit: 10,
    hideSmallGroups: false,
    showCount: true,
    xAxisLabelOverride: "",
    yAxisLabelOverride1: "",
    yAxisLabelOverride2: "",
  },
  metadata: {
    ai_headline: null,
    ai_summary: null,
    ai_headline_dirty: false,
    ai_summary_dirty: false,
    ai_headline_expanded: true,
    ai_summary_expanded: true,
  },
};

export const REPORT_PRESETS: Record<
  string,
  { label: string; config: ReportConfigV2 }
> = {
  REPRESENTATION_REPORT: {
    label: "Representation",
    config: REPRESENTATION_REPORT,
  },
  HIRES_REPORT: {
    label: "Hires",
    config: HIRES_REPORT,
  },
  COMPENSATION_REPORT: {
    label: "Compensation",
    config: COMPENSATION_REPORT,
  },
  PROMOTIONS_REPORT: {
    label: "Promotions",
    config: PROMOTIONS_REPORT,
  },
  TERMINATIONS_REPORT: {
    label: "Terminations",
    config: TERMINATIONS_REPORT,
  },
  ATTRITION_RATE_REPORT: {
    label: "Attrition Rate",
    config: ATTRITION_RATE_REPORT,
  },
  ATTRITION_PROBABILITY_REPORT: {
    label: "Attrition Probability",
    config: ATTRITION_PROBABILITY_REPORT,
  },
  COST_OF_ATTRITION_REPORT: {
    label: "Cost of Attrition",
    config: COST_OF_ATTRITION_REPORT,
  },
  ATTRITION_DRIVERS_REPORT: {
    label: "Attrition Drivers",
    config: ATTRITION_DRIVERS_REPORT,
  },
  RETENTION_RATE_REPORT: {
    label: "Retention Rate",
    config: RETENTION_RATE_REPORT,
  },
  BENCHMARK_REPORT: {
    label: "Benchmark",
    config: BENCHMARK_REPORT,
  },
  APPLICANT_FUNNEL_CURRENT_REPORT: {
    label: "Applicant Funnel (Current)",
    config: APPLICANT_FUNNEL_CURRENT_REPORT,
  },
  APPLICANT_FUNNEL_REPORT: {
    label: "Applicant Funnel (Past Year)",
    config: APPLICANT_FUNNEL_REPORT,
  },
  TIME_TO_FILL_REPORT: {
    label: "Time to Fill",
    config: TIME_TO_FILL_REPORT,
  },
  JOB_REQUISITION_METRICS_REPORT: {
    label: "Job Requisition Metrics",
    config: JOB_REQUISITION_METRICS_REPORT,
  },
};

export const REPORT_PRESET_GROUPS: Record<
  string,
  Array<keyof typeof REPORT_PRESETS>
> = {
  Workforce: ["REPRESENTATION_REPORT", "BENCHMARK_REPORT"],
  "Talent Acquisition": [
    "APPLICANT_FUNNEL_REPORT",
    "APPLICANT_FUNNEL_CURRENT_REPORT",
    "TIME_TO_FILL_REPORT",
    "JOB_REQUISITION_METRICS_REPORT",
  ],
  "Talent Flow": ["HIRES_REPORT", "PROMOTIONS_REPORT", "TERMINATIONS_REPORT"],
  "Retention & Risk": [
    "ATTRITION_RATE_REPORT",
    "ATTRITION_PROBABILITY_REPORT",
    "ATTRITION_DRIVERS_REPORT",
    "RETENTION_RATE_REPORT",
  ],
  "Financial Impact": ["COMPENSATION_REPORT", "COST_OF_ATTRITION_REPORT"],
};

const INITIAL_MAIN_DEFAULT: ReportConfigV2 = {
  title: "Untitled Report",
  data: {
    datasetName: "main",
    measures: [],
    dimensions: [],
    filters: [],
    timeCadence: "Quarter",
    includeProjections: false,
    comparisonEnabled: false,
    compareWith: "previousPeriod",
  },
  chart: {
    chartType: "Bar",
    includeLegend: true,
    legendPosition: "bottom",
    legendItems: {},
    limit: 10,
    hideSmallGroups: false,
    showCount: true,
    xAxisLabelOverride: "",
    yAxisLabelOverride1: "",
    yAxisLabelOverride2: "",
  },
  metadata: {
    ai_headline: null,
    ai_summary: null,
    ai_headline_dirty: false,
    ai_summary_dirty: false,
    ai_headline_expanded: true,
    ai_summary_expanded: true,
  },
};

const INITIAL_LIGHTCAST_REPORT: ReportConfigV2 = {
  title: "Untitled Report",
  data: {
    datasetName: "lightcast",
    measures: [],
    dimensions: [],
    filters: [],
    timeCadence: "Quarter",
    includeProjections: false,
    comparisonEnabled: false,
    compareWith: "previousPeriod",
  },
  chart: {
    chartType: "Bar",
    includeLegend: true,
    legendPosition: "bottom",
    legendItems: {},
    limit: 10,
    hideSmallGroups: false,
    showCount: true,
    xAxisLabelOverride: "",
    yAxisLabelOverride1: "",
    yAxisLabelOverride2: "",
  },
  metadata: {
    ai_headline: null,
    ai_summary: null,
    ai_headline_dirty: false,
    ai_summary_dirty: false,
    ai_headline_expanded: true,
    ai_summary_expanded: true,
  },
};

const INITIAL_OSHA_INCIDENTS_REPORT: ReportConfigV2 = {
  title: "Untitled Report",
  data: {
    datasetName: "osha_incidents",
    measures: ["INCIDENT_COUNT"],
    dimensions: ["OSHA_INCIDENT_DATE"],
    filters: [],
    timeCadence: "Quarter",
    includeProjections: false,
    comparisonEnabled: false,
    compareWith: "previousPeriod",
  },
  chart: {
    chartType: "Bar",
    includeLegend: true,
    legendPosition: "bottom",
    legendItems: {},
    limit: 10,
    hideSmallGroups: false,
    showCount: true,
    xAxisLabelOverride: "",
    yAxisLabelOverride1: "",
    yAxisLabelOverride2: "",
  },
  metadata: {
    ai_headline: null,
    ai_summary: null,
    ai_headline_dirty: false,
    ai_summary_dirty: false,
    ai_headline_expanded: true,
    ai_summary_expanded: true,
  },
};

const INITIAL_EMPLOYEE_RELATIONS_REPORT: ReportConfigV2 = {
  title: "Untitled Report",
  data: {
    datasetName: "employee_relations",
    measures: [],
    dimensions: [],
    filters: [],
    timeCadence: "Quarter",
    includeProjections: false,
    comparisonEnabled: false,
    compareWith: "previousPeriod",
  },
  chart: {
    chartType: "Bar",
    includeLegend: true,
    legendPosition: "bottom",
    legendItems: {},
    limit: 10,
    hideSmallGroups: false,
    showCount: true,
    xAxisLabelOverride: "",
    yAxisLabelOverride1: "",
    yAxisLabelOverride2: "",
  },
  metadata: {
    ai_headline: null,
    ai_summary: null,
    ai_headline_dirty: false,
    ai_summary_dirty: false,
    ai_headline_expanded: true,
    ai_summary_expanded: true,
  },
};
const INITIAL_EMPLOYEE_GOALS_REPORT: ReportConfigV2 = {
  title: "Untitled Report",
  data: {
    datasetName: "employee_goals",
    measures: [],
    dimensions: [],
    filters: [],
    timeCadence: "Quarter",
    includeProjections: false,
    comparisonEnabled: false,
    compareWith: "previousPeriod",
  },
  chart: {
    chartType: "Bar",
    includeLegend: true,
    legendPosition: "bottom",
    legendItems: {},
    limit: 10,
    hideSmallGroups: false,
    showCount: true,
    xAxisLabelOverride: "",
    yAxisLabelOverride1: "",
    yAxisLabelOverride2: "",
  },
  metadata: {
    ai_headline: null,
    ai_summary: null,
    ai_headline_dirty: false,
    ai_summary_dirty: false,
    ai_headline_expanded: true,
    ai_summary_expanded: true,
  },
};

const INITIAL_CULTURE_DEFAULT: ReportConfigV2 = {
  title: "Untitled Report",
  data: {
    datasetName: "culture",
    measures: ["SURVEY_SCORE"],
    dimensions: ["ANSWER_VALUE", "QUESTION_THEME"],
    filters: [
      {
        filterDefinitionName: "SURVEY_NAME",
        operator: "IN",
        value: [],
      },
      {
        filterDefinitionName: "QUESTION_THEME",
        operator: "IN",
        value: [],
      },
    ],
    timeCadence: "Quarter",
    includeProjections: false,
    comparisonEnabled: false,
    compareWith: "previousPeriod",
  },
  chart: {
    chartType: "Horizontal Stacked Bar",
    includeLegend: true,
    legendPosition: "bottom",
    legendItems: {},
    limit: 10,
    hideSmallGroups: false,
    showCount: true,
    xAxisLabelOverride: "",
    yAxisLabelOverride1: "",
    yAxisLabelOverride2: "",
  },
  metadata: {
    ai_headline: null,
    ai_summary: null,
    ai_headline_dirty: false,
    ai_summary_dirty: false,
    ai_headline_expanded: true,
    ai_summary_expanded: true,
  },
};

const TIME_TO_FILL_DRIVERS_DEFAULT: ReportConfigV2 = {
  title: "Top Drivers of Longer Time To Fill",
  data: {
    datasetName: "time_to_fill_drivers",
    measures: ["TIME_TO_FILL_DRIVERS"],
    dimensions: [],
    filters: [],
    timeCadence: "Quarter",
    includeProjections: false,
    comparisonEnabled: false,
    compareWith: "previousPeriod",
  },
  chart: {
    chartType: "Bar",
    includeLegend: true,
    legendPosition: "bottom",
    legendItems: {},
    limit: 30,
    hideSmallGroups: false,
    showCount: true,
    xAxisLabelOverride: "",
    yAxisLabelOverride1: "",
    yAxisLabelOverride2: "",
  },
  metadata: {
    ai_headline: null,
    ai_summary: null,
    ai_headline_dirty: false,
    ai_summary_dirty: false,
    ai_headline_expanded: true,
    ai_summary_expanded: true,
  },
};

const RECRUITMENT_APPLICATIONS_DEFAULT: ReportConfigV2 = {
  title: "Untitled Report",
  data: {
    datasetName: "recruitment_applications",
    measures: [],
    dimensions: [],
    filters: [],
    timeCadence: "Quarter",
    includeProjections: false,
    comparisonEnabled: false,
    compareWith: "previousPeriod",
  },
  chart: {
    chartType: "Bar",
    includeLegend: true,
    legendPosition: "bottom",
    legendItems: {},
    limit: 10,
    hideSmallGroups: false,
    showCount: true,
    xAxisLabelOverride: "",
    yAxisLabelOverride1: "",
    yAxisLabelOverride2: "",
  },
  metadata: {
    ai_headline: null,
    ai_summary: null,
    ai_headline_dirty: false,
    ai_summary_dirty: false,
    ai_headline_expanded: true,
    ai_summary_expanded: true,
  },
};

export const INITIAL_REPORT_CONFIGURATIONS: Record<
  DatasetName,
  ReportConfigV2
> = {
  main: INITIAL_MAIN_DEFAULT,
  recs: INITIAL_MAIN_DEFAULT,
  benchmark: BENCHMARK_REPORT,
  attrition_drivers: ATTRITION_DRIVERS_REPORT,
  attrition_directional_drivers: INITIAL_MAIN_DEFAULT, // not used, see getInitialReportConfig function
  methodology: INITIAL_MAIN_DEFAULT, // not used, see getInitialReportConfig function
  recruitment_applications: RECRUITMENT_APPLICATIONS_DEFAULT,
  time_to_fill_drivers: TIME_TO_FILL_DRIVERS_DEFAULT,
  faq: INITIAL_MAIN_DEFAULT, // not used, see getInitialReportConfig function
  culture: INITIAL_CULTURE_DEFAULT,
  osha_incidents: INITIAL_OSHA_INCIDENTS_REPORT,
  employee_relations: INITIAL_EMPLOYEE_RELATIONS_REPORT,
  employee_goals: INITIAL_EMPLOYEE_GOALS_REPORT,
  lightcast: INITIAL_LIGHTCAST_REPORT,
};

export const getInitialReportConfig = (
  datasetName: DatasetName,
): ReportConfigV2 => {
  if (datasetName === "attrition_directional_drivers") {
    // Users cannot select this report type
    throw new Error(
      "No initial report configuration for dataset: " + datasetName,
    );
  }
  return JSON.parse(
    JSON.stringify(INITIAL_REPORT_CONFIGURATIONS[datasetName]),
  ) as ReportConfigV2;
};

/**
 * DEPRECATED
 */

type ReportCategory =
  | "Representation"
  | "Recruitment"
  | "Compensation"
  | "Attrition Rate"
  | "Attrition Probability"
  | "Attrition Top Drivers"
  | "Retention Rate"
  | "Cost of Attrition"
  | "Terminations"
  | "Promotions"
  | "Benchmark"
  | "Belonging"
  | "Engagement"
  | "Satisfaction";
// | "Custom";

export interface Report {
  id: string;
  title: string;
  config: ReportConfig;
  created_at: Date;
  updated_at: Date;
  account_id: string;
  user_id: string;
  system_report: boolean;
  system_report_name?: string;
}

interface MeasureConfig {
  field?: string;
  aggregation: MeasureAggregation;
}

interface ReportDataSettings {
  dataset: string;
  category: ReportCategory;
  dimensions: (string | TimeDimension)[];
  filters: Array<{
    field: string;
    type: FieldType;
    operator: FilterOperator;
    value: ReportFilterValue;
    secondaryValue?: ReportFilterSecondaryValue;
  }>;
  includeProjections?: boolean;
  measureConfig?: MeasureConfig;
}

type MeasureAggregation = "Average" | "Count" | "Count Distinct" | "Sum";

interface ReportDataSettingsWithOptionalCategory extends Omit<
  ReportDataSettings,
  "category"
> {
  category?: ReportCategory;
}

interface ReportChartSettings {
  chartType: ChartTypeName;
  hideMostRecentPeriod?: boolean;
  hideSmallGroups?: boolean;
  includeLegend: boolean;
  legendItems: {
    [key: string]: { value: string; color: string; hidden?: boolean };
  };
  legendPosition: "top" | "bottom" | "left" | "right";
  limit: number;
  showTooltip?: boolean;
  showXAxis?: boolean;
  showYAxis?: boolean;
  // When true, charts/tables display numeric counts (value_num) instead of percentages (value_pct)
  showCount?: boolean;
  xAxisLabel: string;
  yAxisLabel:
    | string
    | {
        value_num: string;
        value_pct: string;
      };
}

interface ReportMetadata {
  ai_headline?: string;
  ai_summary?: string;
  ai_headline_dirty?: boolean;
  ai_summary_dirty?: boolean;
  ai_headline_expanded?: boolean; // Flag to track if AI headline is expanded in UI
  ai_summary_expanded?: boolean; // Flag to track if AI summary is expanded in UI
}

export interface ReportConfig {
  id?: string;
  chart: ReportChartSettings;
  data: ReportDataSettings;
  metadata?: ReportMetadata;
  title: string;
  _titleManuallySet?: boolean; // Flag to track if title has been manually edited
  system_report_name?: string; // Name of the system report if this is a system report
}

export interface ReportConfigWithOptionalCategory extends Omit<
  ReportConfig,
  "data"
> {
  data: ReportDataSettingsWithOptionalCategory;
}
